import { Accordion, Flex, Text, Loader } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import TrainingTimelineView from '../../components/TrainingTimeLineView';
import colors from '../../utils/colors';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInFromLeft, fadeInFromTop, ctaPulse, heartbeatSmall, heartbeat } from '../../utils/animations';
import { useState, useEffect } from 'react';

import MachinesList from './MachinesList/MachinesList';
import { useTrainings, TrainingSummary } from '../../hooks/useTrainings';
import { useTrainingSessions } from '../../hooks/useTrainingSessions';

const Training: React.FC = () => {
  const nav = useNavigate();
  const trainingsQuery = useTrainings();
  const isLoading = trainingsQuery.isLoading;
  const trainings = trainingsQuery.data?.list || [];
  const activeTrainingId = trainingsQuery.data?.activeTrainingId || null;
  const activeTrainingDetail = useTrainingSessions(activeTrainingId || 0);
  const { translate } = useLanguage();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [machinesLoaded, setMachinesLoaded] = useState(false);

  // const { state } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleMachinesLoadingComplete = (isComplete: boolean) => {
    if (isComplete) {
      setTimeout(() => setShouldAnimate(true), 100);
    }
    setMachinesLoaded(isComplete);
  };

  const TrainingAccordionItem = ({ t }: { t: TrainingSummary }) => {
    const { data: detail, isLoading } = useTrainingSessions(t.id);
    const completionDates: Record<string, Date> = {};
    if (detail && detail.completedSessions) {
      Object.entries(detail.completedSessions).forEach(([sid, dt]) => {
        completionDates[sid] = new Date(dt);
      });
    }
    const computedIndex = detail ? Math.max(0, detail.sessions.findIndex((s) => !s.isCompleted)) : null;
    const completedCount = detail ? (detail.sessions?.filter(s => s.isCompleted).length) ?? 0 : 0;
    const progressText = detail ? 
      t.finishedAt 
        ? `${completedCount}/${detail.sessions?.length ?? 0}` // For finished trainings, show completed count
        : `${(computedIndex !== null && computedIndex >= 0 ? computedIndex + 1 : completedCount)}/${detail.sessions?.length ?? 0}` // For active trainings, show active session
      : undefined;
    const activeIndex = detail && activeTrainingId === t.id && !t.finishedAt ? computedIndex : null;

    return (
      <Accordion.Item 
        value={String(t.id)}
        mb={'xs'}
        style={{ 
          borderLeft: `5px solid ${t.finishedSections === t.totalSections || t.finishedAt ? colors.green : colors.primary}`,
          borderRight: `5px solid ${t.finishedSections === t.totalSections || t.finishedAt ? colors.green : colors.primary}`,
          borderRadius: '10px',
        }}
      >
        <Accordion.Control>
          <Flex justify="space-between" align="center" w="100%">
            <Flex direction="column" gap={2} className="w-[75%]">
              <span className="font-semibold text-custom36p text-lg">{t.title}</span>
              {t.subtitle && (
                <span className="text-sm text-gray-500">{t.subtitle}</span>
              )}
              <Text size="sm" c={t.finishedAt ? "dimmed" : colors.primary} fw={t.finishedAt ? 400 : 600}>
                {t.finishedAt
                  ? `${new Date(t.createdAt).toLocaleDateString()} - ${new Date(t.finishedAt).toLocaleDateString()}`
                  : `${new Date(t.createdAt).toLocaleDateString()} - AKTYWNY`}
              </Text>
            </Flex>
            <Flex direction="column" align="flex-end" className="w-[25%]" mr={10}>
              {progressText && (
                <Text fw={700} c={colors.custom36p} size="xl">
                  {progressText}
                </Text>
              )}
            </Flex>
          </Flex>
        </Accordion.Control>
        <Accordion.Panel>
          {isLoading && (
            <div className="flex justify-center py-4"><Loader size="sm"/></div>
          )}
          {detail && (
            <TrainingTimelineView
              sessions={detail.sessions as any}
              activeIndex={activeIndex ?? null}
              completionDates={completionDates}
              onSessionClick={(sid) => nav(`/trening/sesja/${t.id}/${sid}`)}
              isFinishedPlan={!!t.finishedAt}
            />
          )}
        </Accordion.Panel>
      </Accordion.Item>
    );
  };

  return (
    <motion.div 
      className="p-4 mt-5"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
    >
      {machinesLoaded && (
        <>
          {trainings.length > 0 && (
            <motion.div variants={fadeInFromLeft}>
              <Text 
                size="lg" 
                fw={500} 
                c={colors.custom36s} 
                className="text-center"
                mb={2}
              >
                {translate('yourTrainings')}
              </Text>
            </motion.div>
          )}

          {trainings.length > 0 && activeTrainingId && (
            <motion.div 
              className="flex justify-center mt-2 mb-4"
              variants={fadeInFromLeft}
            >
              <motion.button
                className={"uppercase text-white bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:from-custom36s hover:to-custom36p focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p/50 font-bold rounded-lg text-sm px-4 py-1.5 flex items-center justify-center"}
                onClick={() => {
                  const detail = activeTrainingDetail.data;
                  if (!detail) return;
                  const idx = Math.max(0, detail.sessions.findIndex((s) => !s.isCompleted));
                  const targetSessionId = detail.sessions[idx]?.id;
                  if (targetSessionId) {
                    nav(`/trening/sesja/${activeTrainingId}/${targetSessionId}`);
                  }
                }}
                disabled={activeTrainingDetail.isLoading || !activeTrainingDetail.data}
                whileTap={{ scale: 0.95 }}
                variants={heartbeat}
                animate="animate"
              >
                {translate('goToActiveSession')}
              </motion.button>
            </motion.div>
          )}

          <div className="overflow-x-hidden">
            {isLoading && (
              <div className="flex justify-center py-6"><Loader color={colors.custom36p}/></div>
            )}
            {!isLoading && trainings && trainings.length > 0 && (
              <Accordion variant="separated" radius="md" multiple>
                {trainings.map((t) => (
                  <motion.div key={t.id} variants={fadeInFromLeft}>
                    <TrainingAccordionItem t={t} />
                  </motion.div>
                ))}
              </Accordion>
            )}
          </div>

          {/* <motion.div variants={fadeInFromBottom}>
            <UnderConstruction/>
          </motion.div> */}
        </>
      )}

      <MachinesList onLoadingComplete={handleMachinesLoadingComplete} />
    </motion.div>
  );
};

export default Training;
